import { useRef, useState } from 'react';

import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';

import {
  DialogBody,
  DialogContent,
  DialogHeader,
  DialogTitle,
  Dialog as UiDialog,
} from '@/components/ui/dialog';
import { useFeatureFlag } from '@/hooks/useFeatureFlag';

import { NewSemenForm } from '../new-semen-form';
import { SemenFormValues } from '../new-semen-form/schema';

type NewSemenDialogProps = {
  visible: boolean;
  onHide: () => void;
  initialValues?: Partial<SemenFormValues>;
  onSuccess?: (data: SemenFormValues) => void;
  onError?: (error: unknown) => void;
  'data-testid'?: string;
};

export const NewSemenDialog = ({
  visible,
  onHide,
  initialValues,
  onSuccess,
  onError,
  'data-testid': dataTestId,
}: NewSemenDialogProps) => {
  const { t } = useTranslation();
  const dialogRef = useRef<Dialog>(null);
  const [dialogContainer, setDialogContainer] = useState<HTMLElement>();
  const [baseDialogContainer, setBaseDialogContainer] =
    useState<HTMLElement | null>(null);
  const isBaseUi = useFeatureFlag('new_semen_prime_to_base_ui');

  if (isBaseUi) {
    return (
      <UiDialog
        open={visible}
        onOpenChange={(open) => {
          if (!open) onHide();
        }}
      >
        <DialogContent
          preflight={false}
          ref={setBaseDialogContainer}
          className="z-[2000] md:max-w-[1000px]"
          overlayClassName="z-[2000]"
          data-testid={dataTestId}
        >
          <DialogHeader className="preflight">
            <DialogTitle>{t('semen.newSemen')}</DialogTitle>
          </DialogHeader>
          <DialogBody>
            <NewSemenForm
              initialValues={initialValues}
              onCancel={onHide}
              onSuccess={onSuccess}
              onError={onError}
              calendarContainer={baseDialogContainer ?? undefined}
            />
          </DialogBody>
        </DialogContent>
      </UiDialog>
    );
  }

  return (
    <Dialog
      ref={dialogRef}
      header={t('semen.newSemen')}
      visible={visible}
      onHide={onHide}
      modal
      className="w-[1000px] max-w-[95vw]"
      data-testid={dataTestId}
      onShow={() =>
        setDialogContainer(dialogRef.current?.getMask() ?? undefined)
      }
    >
      <NewSemenForm
        initialValues={initialValues}
        onCancel={onHide}
        onSuccess={onSuccess}
        onError={onError}
        calendarContainer={dialogContainer}
      />
    </Dialog>
  );
};
